import React, { FunctionComponent } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from 'styled-components';

interface AchievementCircleIconProps {
  className?: string;
  max: number;
  current: number;
  monochrome?: boolean;
  customSize?: number;
  customMax?: number;
  disabled?: boolean;
}

const AchievementCircleIcon: FunctionComponent<AchievementCircleIconProps> = ({
  className,
  max,
  current,
  monochrome,
  customSize,
  customMax,
  disabled,
}) => {
  const theme = useTheme();

  const circle1 = {
    hidden: {
      pathLength: 0,
    },
    visible: (custom: number) => ({
      pathLength: custom > 0 ? custom : 0,
      transition: {
        duration: 10,
        type: 'spring',
        stiffness: 50,
      },
    }),
    exit: {
      pathLength: 0,
    },
  };

  const circle2 = {
    hidden: {
      pathLength: 0,
    },
    visible: (custom: number) => ({
      pathLength: custom > 0 ? custom : 0,
      transition: {
        duration: 10,
        type: 'spring',
        stiffness: 50,
      },
    }),
  };

  const SPACING = 0.075;

  const getPathColor = () => {
    if (monochrome && current === max) {
      if (disabled) {
        return theme.colors.black;
      }
      return theme.colors.white;
    }
    if (monochrome) {
      if (disabled) {
        return theme.colors.black;
      }
      return theme.colors.white;
    }
    return theme.colors.blueColor;
  };

  const getGrayCircleLength = (
    max: number,
    current: number,
    customMax?: number
  ) => {
    return (1 - current / (customMax ? customMax : max)).toFixed(2);
  };

  const getBlueCircleLength = (
    max: number,
    current: number,
    customMax?: number
  ) => {
    if (current < max || (customMax && current < customMax)) {
      return (current / (customMax ? customMax : max) - SPACING).toFixed(2);
    }
    return 1;
  };

  return (
    <motion.svg
      className={className || ''}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 47 47"
      width={customSize ? customSize : 47}
      height={customSize ? customSize : 47}
      animate="visible"
      initial="hidden"
    >
      <motion.path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="3px"
        fill="none"
        stroke={disabled ? theme.colors.tertiary : '#e0e1e6'}
        d="M18.93,1.99C7.05,4.51-.54,16.19,1.99,28.08s14.21,19.47,26.09,16.95,19.47-14.21,16.95-26.09C42.5,7.05,30.82-.54,18.93,1.99"
        variants={circle1}
        opacity={monochrome ? 0.5 : 1}
        custom={getGrayCircleLength(max, current, customMax)}
      />
      <motion.path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="3px"
        fill="none"
        stroke={getPathColor()}
        d="M23.5,1.5c12.15,0,22,9.85,22,22s-9.85,22-22,22S1.5,35.65,1.5,23.5,11.35,1.5,23.5,1.5"
        variants={circle2}
        custom={getBlueCircleLength(max, current, customMax)}
      />
    </motion.svg>
  );
};

export default AchievementCircleIcon;
